import type { FC } from 'react';

import { ToastProviderContent } from './toast-provider-content';
import { ToastProviderItem } from './toast-provider-item';
import { ToastProviderList } from './toast-provider-list';
import { ToastProviderPortal } from './toast-provider-portal';
import { ToastProviderRoot } from './toast-provider-root';
import type { ToastProviderPropsInterface } from './types/toast-provider.types';

/**
 * `ToastProvider` manages the state for all `Toast` components
 * in the application. A `Toast` component is not added to the
 * page itself, rather it is enqueued and displayed by the provider. A provider is necessary to make
 * sure that toasts from different parts of the application do not conflict with each other.
 *
 * `ToastProvider` uses [React context](https://reactjs.org/docs/context.html) to manage application
 * state. Wrapping your application with `ToastProvider` works similarly to how
 * [`Context.Provider`](https://reactjs.org/docs/context.html#contextprovider) sets up context
 * scope. Wrapping your application with a toast provider allows descendants of the provider to
 * enqueue toasts.
 *
 * To enqueue a toast, a descendant of `ToastProvider` must consume the toast provider context.
 * To do this, we provide a `useToast` hook. When using the `useToast` hook
 * `enqueueToast` function can be accessed from the returned `ToastProvider` context. Calling
 * `enqueueToast` with a `Toast` component will add the toast
 * to the providers queue.
 *
 * It is common to have multiple consumers of `useToast` within your application, but
 * you should generally only have one `ToastProvider`.
 *
 * @since 7.4.0
 */
export const ToastProvider: FC<ToastProviderPropsInterface> & {
  Root: typeof ToastProviderRoot;
  List: typeof ToastProviderList;
  Item: typeof ToastProviderItem;
  Portal: typeof ToastProviderPortal;
  Content: typeof ToastProviderContent;
} = (props: ToastProviderPropsInterface) => {
  const { 'aria-live': ariaLive = 'off', children, portal = true, renderToast, ...rest } = props;

  return (
    <ToastProviderRoot {...rest}>
      {children}
      <ToastProviderPortal portal={portal}>
        <ToastProviderContent aria-live={ariaLive}>
          <ToastProviderList renderToast={renderToast} />
        </ToastProviderContent>
      </ToastProviderPortal>
    </ToastProviderRoot>
  );
};

ToastProvider.Content = ToastProviderContent;
ToastProvider.Item = ToastProviderItem;
ToastProvider.List = ToastProviderList;
ToastProvider.Portal = ToastProviderPortal;
ToastProvider.Root = ToastProviderRoot;

ToastProvider.displayName = 'ToastProvider';
